/* Free Gift Promo — see CONTEXT.md and docs/adr/0001-free-gift-promo-not-a-pure-cart-function.md in cranky-health/tms-shopify */

export const FREE_GIFT_VARIANT_ID = 45514091200698;
export const FREE_GIFT_THRESHOLD_CENTS = 14000; // $140 AUD

export type FreeGiftCustomer = { ordersCount: number } | null;

// The Free Gift Promo is AU-market only. `null`/`undefined` (market unresolved, or the
// window.localization global not yet set) is treated as ineligible, same as any other
// non-'au' handle — never add without a confirmed 'au' market.
export type FreeGiftMarket = { handle: string } | null;
export const FREE_GIFT_ELIGIBLE_MARKET_HANDLE = "au";

// Mirrors the shape of SellingPlanAllocation from cart.ts, trimmed to the field we need.
export type MinimalSellingPlanAllocation = { selling_plan?: { id: number } };

export type MinimalCartItem = {
  variant_id: number;
  key?: string;
  line_price?: number;
  selling_plan_allocation?: MinimalSellingPlanAllocation;
};
export type MinimalCart = { items: MinimalCartItem[] };

export function findFreeGiftLine(cart: MinimalCart): MinimalCartItem | undefined {
  return cart.items.find((item) => item.variant_id === FREE_GIFT_VARIANT_ID);
}

export function decideFreeGiftAction({
  subtotalCents,
  customer,
  market,
  cart,
}: {
  // Shopify's raw items_subtotal_price, including the gift line's own (undiscounted)
  // price when it's already in the cart — the Discount Function that zeroes it out
  // (see docs/adr/0001 in tms-shopify) only runs at checkout, not here. We net it back
  // out below so the gift's own price can't mask the customer's real spend dropping
  // below threshold.
  subtotalCents: number;
  customer: FreeGiftCustomer;
  market: FreeGiftMarket;
  cart: MinimalCart;
}): "add" | "remove" | "noop" {
  const giftLine = findFreeGiftLine(cart);
  const hasGiftLine = giftLine !== undefined;
  const netSubtotalCents = subtotalCents - (giftLine?.line_price ?? 0);

  // A subscription line (selling_plan_allocation present, same presence check as cart.ts)
  // anywhere in the cart makes the whole cart ineligible for the free gift.
  const hasSubscriptionLine = cart.items.some((item) => Boolean(item.selling_plan_allocation?.selling_plan?.id));

  // A guest (no logged-in customer) can't be checked against order history, so we can't
  // tell them apart from a genuine new customer — treat them as eligible rather than
  // excluding them by default. See tms-shopify#71 (reverses the guest-exclusion from
  // ADR 0001/#55).
  const isEligible = (customer === null || customer.ordersCount === 0) && !hasSubscriptionLine;
  const isOverThreshold = netSubtotalCents >= FREE_GIFT_THRESHOLD_CENTS;
  const isEligibleMarket = market?.handle?.toLowerCase() === FREE_GIFT_ELIGIBLE_MARKET_HANDLE;

  if (!hasGiftLine && isEligible && isOverThreshold && isEligibleMarket) return "add";
  if (hasGiftLine && (!isEligible || !isOverThreshold || !isEligibleMarket)) return "remove";
  return "noop";
}
